import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '@store/StoreProvider';
import { applyFxLiteClass, removeFxLiteClass } from '@utils/fxLite';
import './landing.css';
import { IconSprite } from './IconSprite';
import { FxBackground } from './FxBackground';
import { initLandingAnchorLinks, scrollToLocationHash } from './sectionNav';
import { LandingHeader } from './LandingHeader';
import { LandingFooter } from './LandingFooter';
import { HeroSection } from './sections/HeroSection';
import { ImmunitySection } from './sections/ImmunitySection';
import { FeaturesSection } from './sections/FeaturesSection';
import { HowSection } from './sections/HowSection';
import { PricingSection } from './sections/PricingSection';
import { TrialSection } from './sections/TrialSection';
import { CompareSection } from './sections/CompareSection';
import { PrivacySection } from './sections/PrivacySection';
import { FaqSection } from './sections/FaqSection';
import { CtaSection } from './sections/CtaSection';
import { homeTitle } from '@constants/seo';
import {
  initScrollTop,
  initGsapAnimations,
  initBentoTilt,
  initChatDemo,
  initCtaFlow,
} from './landingEffects';

const LandingPage = observer(() => {
  const { i18n } = useStore();
  const [email, setEmail] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = homeTitle(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    document.documentElement.classList.add('landing-active');
    applyFxLiteClass();
    const cleanScrollTop = initScrollTop();
    // Arrived via `/#section` (header link from another page, external link)? Keep
    // the scroll where the hash handler will put it instead of jumping to the top.
    if (window.location.hash.length <= 1) window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });

    const root = rootRef.current;
    if (!root) {
      return () => {
        document.documentElement.classList.remove('landing-active');
        removeFxLiteClass();
        cleanScrollTop();
      };
    }

    let cancelled = false;
    let cleanGsap = () => {};
    let cleanTilt = () => {};
    let cleanChat = () => {};
    let cleanCta = () => {};
    let cleanAnchors = () => {};
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled || !rootRef.current) return;
        const root = rootRef.current;
        // Sections are in the DOM now — honour `/#pricing` etc. before the
        // scroll-driven animations get their first measurement.
        scrollToLocationHash();
        cleanAnchors = initLandingAnchorLinks(root);
        cleanGsap = initGsapAnimations(root);
        cleanTilt = initBentoTilt(root);
        cleanChat = initChatDemo(root);
        const ctaCanvas = root.querySelector('#ctaFlow') as HTMLCanvasElement | null;
        if (ctaCanvas) cleanCta = initCtaFlow(ctaCanvas);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      document.documentElement.classList.remove('landing-active');
      removeFxLiteClass();
      cleanScrollTop();
      cleanAnchors();
      cleanGsap();
      cleanTilt();
      cleanChat();
      cleanCta();
    };
  }, []);

  return (
    <div ref={rootRef} className="landing-page font-sans antialiased overflow-x-hidden relative">
      <IconSprite />
      <FxBackground />
      <LandingHeader />
      <HeroSection email={email} onEmailChange={setEmail} />
      <ImmunitySection />
      <FeaturesSection />
      <HowSection />
      <PricingSection />
      <TrialSection />
      <CompareSection />
      <PrivacySection />
      <FaqSection />
      <CtaSection />
      <LandingFooter />
    </div>
  );
});

export default LandingPage;
